import React, { Component } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { bool, shape, func } from "prop-types";
import { isSameDay, isPast } from "date-fns";
import isEmpty from "lodash/isEmpty";
import I18n from "pages/_components/I18n";
import FieldError from "pages/_components/fields/FieldError";
import MessageFC from "pages/forms/_components/_fields/_scheduler/MessageFC";
import SchedulerModal from "pages/forms/_components/_scheduler/SchedulerModal";
import * as schedulerUtils from "util/scheduler";
import * as dateUtils from "util/date";
import * as configUtils from "util/config";
import FieldLabel from "pages/_components/fields/FieldLabel";
import Image from "pages/_components/Image";
import { selectors as formSelectors } from "reducers/form";
import { actions as transferActions } from "reducers/transfers";
import SchedulerSidebar from "../_scheduler/SchedulerSidebar";

const FORM_ID = "scheduler";

class Scheduler extends Component {
    static propTypes = {
        data: shape({}).isRequired,
        field: shape({}).isRequired,
        form: shape({}).isRequired,
        showLabel: bool,
        hiddenLabel: bool,
        persisData: bool,
        persisDataOther: bool,
        dispatch: func.isRequired,
        programTrasferData: shape({}),
        programTrasferDataOther: shape({}),
        showAsSidebar: bool,
        handleChange: func,
        prevRoute: shape({}).isRequired,
    };

    static defaultProps = {
        showLabel: true,
        hiddenLabel: false,
        persisData: false,
        persisDataOther: false,
        programTrasferData: null,
        programTrasferDataOther: null,
        showAsSidebar: false,
        handleChange: () => {},
    };

    state = {
        errors: null,
        isDisplayed: false,
    };

    componentDidMount() {
        const { field, form, prevRoute } = this.props;
        const { prevPath = "" } = prevRoute;
        const configTime = configUtils.get("transfer.schedule.time", "08:00");
        const timeByZone = dateUtils.getDefaultTimeByZone(configTime);
        const fromOtherPage = !prevPath.split("/").includes("transfers");
        if (field.value === null || fromOtherPage) {
            form.setFieldValue(field.name, {
                selectedOption: schedulerUtils.TODAY,
                valueDate: this.getDefaultValueDate(),
                time: timeByZone,
            });
        }
    }

    static getDerivedStateFromProps(props) {
        const {
            form: { errors },
        } = props;

        const programErrors = Object.keys(errors)
            .filter((key) => key.includes("program@"))
            .reduce(
                (obj, key) => ({
                    ...obj,
                    [key]: errors[key],
                }),
                {},
            );

        return {
            errors: programErrors,
        };
    }

    getDefaultValueDate() {
        const {
            data: { firstWorkingDate },
        } = this.props;

        return moment(firstWorkingDate, "YYYY-MM-DD");
    }

    handleClick = () => {
        this.setState({ isDisplayed: true });
    };

    handleErrors = () => {
        const { errors } = this.state;
        if (!isEmpty(errors)) {
            return (
                <div className="form-group has-error">
                    {Object.keys(errors).map((error) => (
                        <div className="form-group has-error">
                            <FieldError error={errors[error]} />
                        </div>
                    ))}
                </div>
            );
        }

        return null;
    };

    handleValueChange = (data) => {
        const { field, form, persisData, dispatch, persisDataOther, handleChange } = this.props;
        const configTime = configUtils.get("transfer.schedule.time", "08:00");
        const timeByZone = dateUtils.getDefaultTimeByZone(configTime);

        handleChange(data);
        if (data.frequency === schedulerUtils.ONCE) {
            form.setFieldValue(field.name, {
                ...field.value,
                selectedOption: data.frequency,
                valueDate: moment(data.startsOn),
                time: timeByZone,
                program: null,
            });
            if (persisData) {
                dispatch(
                    transferActions.setProgramTrasferData({
                        ...field.value,
                        selectedOption: data.frequency,
                        valueDate: moment(data.startsOn),
                        time: timeByZone,
                        program: null,
                    }),
                );
            }
            if (persisDataOther) {
                dispatch(
                    transferActions.setProgramTrasferDataOther({
                        ...field.value,
                        selectedOption: data.frequency,
                        valueDate: moment(data.startsOn),
                        time: timeByZone,
                        program: null,
                    }),
                );
            }
        } else {
            const newProgramData = {
                frequency: data.frequency === schedulerUtils.CUSTOM ? data.customFreq : data.frequency, // one of DAY, WEEK, MONTH, YEAR
                frequencyValue: data.frequency === schedulerUtils.CUSTOM ? data.customFreqValue : "1", // int that represents the frequency, frequency = DAY +  frequencyValue = 2 => every 2 days
                days: dateUtils.getDayFromDate(data.startsOn), // int that represents the days of the week used by the java class Days at the backend. Same class used by the calendar restriction
                day: data.startsOn.getDate(), // day of the month.
                occurrence: null, // occurrence=3 + days=4 every 3rd WEDNESDAY of the month
                startsOn: data.startsOn,
                lapse: data.lapse.lapse, // no change at all
                number: data.lapse.number,
                date: data.lapse.date,
                isCustom: data.frequency === schedulerUtils.CUSTOM,
            };
            if (newProgramData.frequency === schedulerUtils.WEEK && newProgramData.isCustom) {
                // if it's custom, I take the days from the bitwisedays control
                newProgramData.days = data.days;
            }
            if (newProgramData.frequency === schedulerUtils.MONTH && data.occurrenceType === "occurrence") {
                newProgramData.occurrence = dateUtils.weekOfMonth(moment(data.startsOn));
                newProgramData.day = null;
            }
            form.setFieldValue(field.name, {
                ...field.value,
                selectedOption: newProgramData.frequency,
                program: newProgramData,
                valueDate: moment(data.startsOn),
                time: timeByZone,
            });
            if (persisData) {
                dispatch(
                    transferActions.setProgramTrasferData({
                        ...field.value,
                        selectedOption: newProgramData.frequency,
                        program: newProgramData,
                        valueDate: moment(data.startsOn),
                        time: timeByZone,
                    }),
                );
            }
            if (persisDataOther) {
                dispatch(
                    transferActions.setProgramTrasferDataOther({
                        ...field.value,
                        selectedOption: newProgramData.frequency,
                        program: newProgramData,
                        valueDate: moment(data.startsOn),
                        time: timeByZone,
                    }),
                );
            }
        }
        form.setTouched({ ...form.touched, [field.name]: true });
        this.handleCloseBottomSheetClick();
    };

    handleCloseBottomSheetClick = () => {
        this.setState({ isDisplayed: false });
    };

    setIsDisplayed = (newValue) => {
        this.setState({ isDisplayed: newValue });
    };

    updatePastDate(value) {
        if (value && value.valueDate) {
            const currentDate = new Date();
            if (isPast(value.valueDate) && !isSameDay(currentDate.getTime(), value.valueDate)) {
                const { form } = this.props;
                form.values.scheduler.valueDate = new Date();
            }
        }
    }

    render() {
        const {
            data: { mode, programable, schedulable },
            field: { value },
            showLabel,
            hiddenLabel,
            programTrasferData,
            persisData,
            persisDataOther,
            programTrasferDataOther,
            field,
            showAsSidebar,
        } = this.props;
        const { errors, isDisplayed } = this.state;
        this.updatePastDate(value);
        let dataToUse = field?.value;
        if (persisData) {
            dataToUse = programTrasferData;
        }
        if (persisDataOther) {
            dataToUse = programTrasferDataOther;
        }
        if (programable || schedulable) {
            if (mode === "edit") {
                return (
                    <>
                        {showAsSidebar ? (
                            <SchedulerSidebar
                                idForm={FORM_ID}
                                setIsDisplayed={this.setIsDisplayed}
                                goBack={this.handleCloseBottomSheetClick}
                                onScheduleClick={this.handleValueChange}
                                value={value}
                                isDisplayed={isDisplayed}
                            />
                        ) : (
                            <SchedulerModal
                                idForm={FORM_ID}
                                handleCloseBottomSheetClick={this.handleCloseBottomSheetClick}
                                goBack={this.handleCloseBottomSheetClick}
                                onScheduleClick={this.handleValueChange}
                                value={value}
                                isDisplayed={isDisplayed}
                            />
                        )}
                        {showLabel && <FieldLabel labelKey="scheduler.label" idField="readableID" />}

                        <div className={`form-group form-group--scheduler${hiddenLabel && " hidden-label"}`}>
                            <div className="scheduler button-group">
                                <button
                                    aria-haspopup="dialog"
                                    className="btn btn-outline"
                                    onClick={this.handleClick}
                                    aria-expanded={isDisplayed}
                                    type="button">
                                    <Image src="images/figma/icon/calendar-primary.svg" className="svg-icon" />
                                    {!dataToUse || dataToUse.selectedOption !== schedulerUtils.TODAY ? (
                                        <>
                                            <I18n id="scheduler.editDate" componentProps={{ "aria-hidden": true }} />
                                            <I18n
                                                id="scheduler.editDate.a11y"
                                                componentProps={{ className: "visually-hidden" }}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <I18n id="scheduler.schedule" componentProps={{ "aria-hidden": true }} />
                                            <I18n
                                                id="scheduler.schedule.a11y"
                                                componentProps={{ className: "visually-hidden" }}
                                            />
                                        </>
                                    )}
                                </button>
                                {!isEmpty(errors) && this.handleErrors()}
                            </div>
                            <span className="scheduler-message">
                                <MessageFC value={field?.value || { valueDate: "" }} />
                            </span>
                        </div>
                    </>
                );
            }
        }

        return null;
    }
}

const mapStateToProps = (state) => ({
    prevRoute: formSelectors.getPrevRoute(state),
});

export default connect(mapStateToProps)(Scheduler);
